/**
 * Kaagazy EMR offline database (Dexie / IndexedDB).
 *
 * This is the foundation for all offline-first functionality. Every clinical
 * entity that must be creatable or editable while offline has a table here,
 * mirroring the server schema plus three sync fields (sync_status, version,
 * local_updated_at).
 *
 * Indexes follow a general rule: PK first, then the FK(s) used for the most
 * common offline queries, then sync_status for queue sweeps.
 *
 * To bump schema shape, add a new .version() block below — never edit an
 * existing one in place (Dexie replays every version on open).
 */

import Dexie, { Table } from "dexie";

import type {
  AllergyOnDisk,
  TreatmentPlanItemOnDisk,
  AppointmentOnDisk,
  DrugOnDisk,
  PatientOnDisk,
  PrescriptionOnDisk,
  SyncQueueOnDisk,
  VisitOnDisk,
  VitalsOnDisk,
} from "./db.types";

export interface DeviceSetting {
  key:   string;
  value: string;
}

// Reference-data records cached for offline use — no encryption needed
// (provider identifiers, not patient data).
export interface CachedUserRecord {
  user_id:             string;
  clinic_id?:          string;
  first_name:          string;
  last_name:           string;
  role:                string;
  specialization?:     string;
  registration_number?: string;
}
export interface CachedClinicRecord {
  clinic_id: string;
  name:      string;
  location:  string;
  phone?:    string;
  email?:    string;
  address?:  string;
}

export interface StoredKey {
  id:  string;
  key: CryptoKey;
}

export const DB_NAME    = "kaagazy_emr";
export const DB_VERSION = 7; // must match the highest .version() block below

// Class is intentionally NOT exported — a single Dexie connection per database
// is required (multiple instances open separate handles, breaking offline
// reads). Import the `db` singleton below instead.
//
// Table types are the OnDisk envelopes (see db.types.ts) — every entity row
// is stored as a slim plaintext slice + an `encrypted_data` blob. App code
// that needs the in-memory shape goes through `./encryption-bridge.ts` to
// translate. Direct Dexie reads/writes bypass encryption — only the
// operations.ts / offline-sync.service.ts boundaries should call .put()/.get()
// directly, and they handle the bridge.
class KaagazyDB extends Dexie {
  patients!:      Table<PatientOnDisk,      string>;
  visits!:        Table<VisitOnDisk,        string>;
  vitals!:        Table<VitalsOnDisk,       string>;
  prescriptions!: Table<PrescriptionOnDisk, string>;
  appointments!:  Table<AppointmentOnDisk,  string>;
  allergies!:     Table<AllergyOnDisk,      string>;
  drugs!:         Table<DrugOnDisk,         string>;
  sync_queue!:    Table<SyncQueueOnDisk,    string>;
  users!:            Table<CachedUserRecord,   string>;
  clinics!:          Table<CachedClinicRecord, string>;
  device_settings!:  Table<DeviceSetting,      string>;
  crypto_keys!:      Table<StoredKey,          string>;
  treatment_plan_items!: Table<TreatmentPlanItemOnDisk, string>;

  constructor() {
    super(DB_NAME);

    // v1 — original plaintext-on-disk shape. Kept here unchanged so Dexie can
    // replay the version chain on existing databases. Any device that opened
    // the DB pre-v2 has rows stored as full plaintext records.
    this.version(1).stores({
      patients:      "patient_id, clinic_id, contact_number, sync_status, local_updated_at",
      visits:        "visit_id, patient_id, doctor_id, status, visit_date, sync_status",
      vitals:        "vital_id, visit_id, sync_status",
      prescriptions: "prescription_id, visit_id, status, sync_status",
      appointments:  "appointment_id, patient_id, doctor_id, appointment_date, status, sync_status",
      allergies:     "allergy_id, patient_id, severity, sync_status",
      drugs:         "drug_id, generic_name, drug_name, sync_status",
      sync_queue:    "id, status, entity_type, [entity_type+entity_id], client_timestamp",
    });

    // v2 — encryption-at-rest. The set of indexed fields is unchanged (every
    // index here was already plaintext in v1 and stays plaintext in the
    // OnDisk envelope), so the schema strings are identical. The shape
    // change is purely structural: PHI fields move into an `encrypted_data`
    // blob handled by `./encryption-bridge.ts`.
    //
    // Migration policy: NO `.upgrade()` callback. Re-encrypting plaintext v1
    // rows in-place from inside Dexie's `.upgrade()` would require a DEK at
    // DB-open time, which races with auth bootstrap. Web Crypto promises
    // also don't participate in Dexie's transaction lifecycle, so awaiting
    // `encrypt(...)` mid-`.upgrade()` risks early auto-commit.
    //
    // Instead, the v1 → v2 migration runs OUTSIDE Dexie via
    // `./v2-migration.ts`. It is triggered by `./security-lifecycle.ts` once
    // auth is established (Hub `signedIn` event + cold-boot probe), reads
    // each entity table, encrypts plaintext rows that lack `encrypted_data`,
    // and writes them back. Idempotent and resumable; completion is
    // recorded in localStorage.
    //
    // The encryption-bridge unwraps tolerate v1 rows (no `encrypted_data`)
    // and pass them through as-is, so reads continue to work during the
    // transition window — the migration eventually rewrites every row but
    // the app doesn't break in the meantime.
    this.version(2).stores({
      patients:      "patient_id, clinic_id, contact_number, sync_status, local_updated_at",
      visits:        "visit_id, patient_id, doctor_id, status, visit_date, sync_status",
      vitals:        "vital_id, visit_id, sync_status",
      prescriptions: "prescription_id, visit_id, status, sync_status",
      appointments:  "appointment_id, patient_id, doctor_id, appointment_date, status, sync_status",
      allergies:     "allergy_id, patient_id, severity, sync_status",
      drugs:         "drug_id, generic_name, drug_name, sync_status",
      sync_queue:    "id, status, entity_type, [entity_type+entity_id], client_timestamp",
    });

    // v3 — reference-data cache for offline use: current user (doctor) and
    // clinic. No sync fields needed — these are pulled on signIn only and
    // cleared on signOut so a subsequent user sees fresh data.
    this.version(3).stores({
      users:   "user_id, clinic_id",
      clinics: "clinic_id",
    });

    // v4 — add display_id plaintext index on patients for offline
    // display_id → UUID resolution (getPatientByDisplayId offline fallback).
    // display_id is a non-PHI opaque public identifier (PAT-XXXXXX); safe to index.
    this.version(4).stores({
      patients: "patient_id, clinic_id, contact_number, sync_status, local_updated_at, display_id",
    });

    // v5 — device_settings: general key/value store for device-scoped config.
    // Used to persist the sync_token so the Service Worker can authenticate
    // batch-push requests without an open tab. The API base URL is NOT stored
    // here — it is delivered via postMessage to SW memory to prevent XSS redirect.
    this.version(5).stores({
      device_settings: "&key",
    });

    // v6 — crypto_keys: persists the per-device DEK as a non-extractable
    // CryptoKey object so offline reads survive a page refresh without
    // re-fetching from the server. CryptoKey is structured-cloneable (Web
    // Crypto spec §7) and can be put/get from IDB even when extractable:false
    // — raw key bytes are never accessible from JS; only SubtleCrypto can use
    // the handle.
    this.version(6).stores({
      crypto_keys: "&id",
    });

    // v7 - treatment_plan_items: a patient's open dental treatment plan,
    // pulled for offline visits (Phase 2). Shipped empty one release before
    // anything writes it. A device that has opened v7 cannot be opened by a
    // build that only knows v6 (Dexie throws VersionError and offline storage
    // stops working), so the bump must not ride in the same release as the
    // first code that needs it: that release must stay revertible.
    this.version(7).stores({
      treatment_plan_items: "id, patient_id, plan_id, sync_status",
    });
  }
}

// Module-level singleton. All app code must import `db` from this file —
// never instantiate KaagazyDB elsewhere (Dexie opens a new connection per
// instance, which breaks offline reads).
export const db = new KaagazyDB();
